/**
 * Two-step dialog launched from the "Update from PitchBook" button on the
 * Company detail PitchBook tab.
 *
 * Step 1 — Pick source: searchable list of indexed PitchBook companies.
 *           Defaults to the company already matched to this CRM account.
 * Step 2 — Review diff: reuses ImportDiffDialog with companyOnly + targetAccountId
 *           so the import is force-linked to THIS company (no match ladder).
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { X, Search, Loader2, Building2, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import ImportDiffDialog from './ImportDiffDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UpdateFromPitchBookDialog({
    accountId,
    accountName,
    defaultPitchbookId,
    onClose,
    onCommitted,
}) {
    const { getAuthHeader } = useAuth();

    // Step 1 state
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [selected, setSelected] = useState(null);   // { pitchbook_id, name, entity }
    const [defaultCandidate, setDefaultCandidate] = useState(null); // pinned current match
    const [errorMsg, setErrorMsg] = useState(null);
    const [orgUsers, setOrgUsers] = useState([]);
    const debounceRef = useRef(null);

    // Step 2 state
    const [showDiff, setShowDiff] = useState(false);

    // Resolve the default (already-matched) company once on mount so it can be
    // pinned to the top even if it falls outside the first alphabetical page.
    useEffect(() => {
        if (!defaultPitchbookId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(
                    `${API}/pitchbook/manual-sync/candidates`,
                    { params: { q: accountName || '', limit: 50 }, headers: getAuthHeader() },
                );
                const match = (res.data?.candidates || [])
                    .find(c => c.pitchbook_id === defaultPitchbookId);
                if (match && !cancelled) {
                    setDefaultCandidate(prev => prev || match);
                    setSelected(prev => prev || match);
                }
            } catch { /* non-fatal — pin falls back to list-found match */ }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(
                    `${API}/admin/target-accounts/stats`,
                    { headers: getAuthHeader() },
                );
                if (!cancelled) setOrgUsers(Array.isArray(res.data) ? res.data : []);
            } catch {
                if (!cancelled) setOrgUsers([]);
            }
        })();
        return () => { cancelled = true; };
    }, [getAuthHeader]);

    // Load candidates on mount and when query changes
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoadingCandidates(true);
            setErrorMsg(null);
            try {
                const res = await axios.get(
                    `${API}/pitchbook/manual-sync/candidates`,
                    { params: { q: query, limit: 50 }, headers: getAuthHeader() },
                );
                const list = res.data?.candidates || [];
                setCandidates(list);

                // Capture the default (already-matched) company once so it can be
                // pinned to the top and pre-selected even after the user searches.
                if (defaultPitchbookId) {
                    const match = list.find(c => c.pitchbook_id === defaultPitchbookId);
                    if (match) {
                        setDefaultCandidate(prev => prev || match);
                        setSelected(prev => prev || match);
                    }
                }
            } catch (e) {
                const status = e.response?.status;
                if (status === 409) {
                    setErrorMsg('No indexed PitchBook data yet — index a feed file first in the Admin PitchBook panel.');
                } else {
                    setErrorMsg('Failed to load PitchBook companies.');
                }
                setCandidates([]);
            } finally {
                setLoadingCandidates(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const handleContinue = () => {
        if (!selected) return;
        setShowDiff(true);
    };

    // When diff dialog commits, propagate up and close everything
    const handleCommitted = () => {
        onCommitted?.();
    };

    // Pin the active selection (or default match) to the top of the list, deduped
    // from the rest so the user never has to search to see what's selected.
    const pinned = selected || defaultCandidate;
    const restCandidates = pinned
        ? candidates.filter(c => c.pitchbook_id !== pinned.pitchbook_id)
        : candidates;

    // While step 2 (ImportDiffDialog) is showing, unmount step 1 portal
    if (showDiff) {
        return (
            <ImportDiffDialog
                pitchbookId={selected.pitchbook_id}
                recordName={selected.name || selected.pitchbook_id}
                targetAccountId={accountId}
                companyOnly
                orgUsers={orgUsers}
                onClose={onClose}
                onCommitted={handleCommitted}
            />
        );
    }

    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Update from PitchBook
                        </h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Pick which PitchBook company to pull data from for{' '}
                            <span className="font-medium text-stone-700">{accountName || 'this company'}</span>.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700 shrink-0 ml-3">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-5 pt-4 pb-2">
                    <div className="relative">
                        <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search PitchBook companies…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 bg-white"
                            autoFocus
                        />
                    </div>
                </div>

                {/* Candidate list */}
                <div className="flex-1 overflow-y-auto mx-5 mb-2 border border-stone-200 rounded-lg" style={{ maxHeight: 320 }}>
                    {loadingCandidates && candidates.length === 0 && (
                        <div className="flex items-center justify-center py-10 text-stone-400">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                    )}

                    {!loadingCandidates && errorMsg && (
                        <div className="px-4 py-6 text-center text-sm text-amber-700 bg-amber-50">
                            {errorMsg}
                        </div>
                    )}

                    {/* Pinned current match / active selection — always visible at top */}
                    {!errorMsg && pinned && (
                        <>
                            <CandidateRow
                                candidate={pinned}
                                isSelected={selected?.pitchbook_id === pinned.pitchbook_id}
                                isDefault={pinned.pitchbook_id === defaultPitchbookId}
                                onSelect={setSelected}
                            />
                            {restCandidates.length > 0 && (
                                <div className="px-4 py-1 bg-stone-50/80 border-b border-stone-100">
                                    <span className="text-[10px] font-semibold tracking-wider uppercase text-stone-400">
                                        Other companies
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    {!loadingCandidates && !errorMsg && restCandidates.length === 0 && !pinned && (
                        <div className="px-4 py-6 text-center text-sm text-stone-400">
                            No companies found{query ? ` for "${query}"` : ''}.
                        </div>
                    )}

                    {!errorMsg && restCandidates.map((c) => (
                        <CandidateRow
                            key={c.pitchbook_id}
                            candidate={c}
                            isSelected={selected?.pitchbook_id === c.pitchbook_id}
                            isDefault={c.pitchbook_id === defaultPitchbookId}
                            onSelect={setSelected}
                        />
                    ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-stone-200">
                    <p className="text-xs text-stone-400">
                        {selected
                            ? <>Selected: <span className="text-stone-700 font-medium">{selected.name || selected.pitchbook_id}</span></>
                            : 'Select a company above'}
                    </p>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={onClose}>Cancel</Button>
                        <Button onClick={handleContinue} disabled={!selected}>
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}

function CandidateRow({ candidate, isSelected, isDefault, onSelect }) {
    return (
        <button
            onClick={() => onSelect(candidate)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left border-b border-stone-100 last:border-0 transition-colors
                ${isSelected ? 'bg-stone-900 text-white' : 'hover:bg-stone-50 text-stone-700'}`}
        >
            <span className="flex items-center gap-2 min-w-0">
                <Building2 className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-stone-400'}`} />
                <span className="text-sm font-medium truncate">{candidate.name || candidate.pitchbook_id}</span>
                {candidate.entity === 'investor' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${
                        isSelected ? 'border-white/30 text-white/70' : 'border-stone-200 text-stone-400'}`}>
                        Investor
                    </span>
                )}
            </span>
            <span className="flex items-center gap-2 shrink-0">
                {isDefault && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                        isSelected ? 'border-white/40 text-white/80' : 'border-emerald-200 text-emerald-600 bg-emerald-50'}`}>
                        Current match
                    </span>
                )}
                {isSelected && <ChevronRight className="w-3.5 h-3.5 text-white/70" />}
            </span>
        </button>
    );
}
